import {
  addChild,
  updateWidgetConfig,
  batchUpdateRepeaterWidgets,
  removeWidget as removeWidgetAction,
} from 'shared/state/ducks/sites';
import { getInitialChildren } from '../../../builder/util/getInitialChildren';
import { v4 as uuidv4 } from 'uuid';
import { widgetTypes } from 'builder/util/constants';

// Lazy import to avoid circular dependency:
// widgets.ts → RepeaterUtils → shared/widgets → ... → widgets.ts
const getWidgetLazy = (widgetType: string, editing: boolean) => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { getWidget } = require('shared/widgets');
  return getWidget(widgetType, editing);
};

export interface PaginationState {
  currentPageNumber: number;
  itemsPerPage: number;
  totalItems: number;
}

export interface PaginatedData<T> {
  items: T[];
  pagination: PaginationState;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalPages: number;
}

/**
 * Resolves the one-based container index for a newly inserted repeater row.
 *
 * @param insertedRowIndex - Zero-based index of the inserted data row.
 * @param existingContainerCount - Number of containers before the update.
 * @param containerCount - Requested number of containers after the update.
 * @returns The one-based insertion index, or null when no container is added.
 */
export const getInsertContainerIndex = (
  insertedRowIndex: number | null,
  existingContainerCount: number,
  containerCount: number
): number | null => {
  if (insertedRowIndex === null || containerCount <= existingContainerCount) {
    return null;
  }

  return insertedRowIndex + 1;
};

export const normalizeTagValue = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase();
};

export const parseDelimitedTags = (value: unknown): string[] => {
  if (typeof value !== 'string') return [];

  return value
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean);
};

export const extractUniqueFilterOptions = (
  rows: any[],
  filterBy: string
): string[] => {
  if (!Array.isArray(rows) || !filterBy) return [];

  const seen = new Set<string>();
  const options: string[] = [];

  rows.forEach(row => {
    parseDelimitedTags(row?.[filterBy]).forEach(tag => {
      const normalized = normalizeTagValue(tag);
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);
      options.push(tag);
    });
  });

  return options;
};

export const rowMatchesSelectedTags = (
  rowValue: unknown,
  selectedOptions: string[]
): boolean => {
  if (!Array.isArray(selectedOptions) || selectedOptions.length === 0) {
    return true;
  }

  const selectedTagSet = new Set(
    selectedOptions.map(option => normalizeTagValue(option)).filter(Boolean)
  );

  if (selectedTagSet.size === 0) return false;

  return parseDelimitedTags(rowValue).some(tag =>
    selectedTagSet.has(normalizeTagValue(tag))
  );
};

interface SyncWidgetAddParams {
  addedWidget: Widget<any>;
  currentPage: PageVersion;
  siteId: number;
  pageId: number;
  dispatch?: any;
}

export function getSortedWidgets(container, currentPage, repeaterWidgetId) {
  return container.children
    .map(id => currentPage.content[id])
    .filter(Boolean)
    .filter(widget => widget.config.repeaterWidgetId === repeaterWidgetId)
    .sort(
      (a, b) =>
        (a.config.repeaterWidgetOrder ?? 0) -
        (b.config.repeaterWidgetOrder ?? 0)
    );
}

export function insertWidgetInContainer({
  newWidget,
  container,
  currentPage,
  dispatch,
  site,
  pageId,
  repeaterWidgetId,
}) {
  const widgets = getSortedWidgets(container, currentPage, repeaterWidgetId);

  let insertIndex = widgets.findIndex(
    w => newWidget.config.yLocation < w.config.yLocation
  );
  if (insertIndex === -1) insertIndex = widgets.length;

  const updatedWidgets = [
    ...widgets.slice(0, insertIndex),
    newWidget,
    ...widgets.slice(insertIndex),
  ];

  updatedWidgets.forEach((widget, i) => {
    dispatch(
      updateWidgetConfig(
        site.siteId,
        pageId,
        widget.id,
        {
          ...widget.config,
          repeaterWidgetId,
          repeaterWidgetOrder: i,
        },
        false
      )
    );
  });
}

export function removeWidgetFromContainer({
  widgetId,
  container,
  currentPage,
  dispatch,
  site,
  pageId,
  repeaterWidgetId,
}) {
  const remainingWidgets = container.children
    .filter(id => id !== widgetId)
    .map(id => currentPage.content[id])
    .filter(Boolean)
    .filter(widget => widget.config.repeaterWidgetId === repeaterWidgetId)
    .sort(
      (a, b) =>
        (a.config.repeaterWidgetOrder ?? 0) -
        (b.config.repeaterWidgetOrder ?? 0)
    );

  remainingWidgets.forEach((widget, i) => {
    dispatch(
      updateWidgetConfig(
        site.siteId,
        pageId,
        widget.id,
        {
          ...widget.config,
          repeaterWidgetOrder: i,
        },
        false
      )
    );
  });
}

/**
 * When a child widget inside a repeater container is deleted, this function
 * finds and deletes the matching widget (by repeaterWidgetId) in all sibling
 * containers, keeping the repeater in sync.
 *
 * @param widgetId - The ID of the widget being deleted
 * @param currentPage - The current page version
 * @param dispatch - Redux dispatch function
 * @param site - The current site object
 */
export function syncDeleteAcrossRepeaterContainers({
  widgetId,
  currentPage,
  dispatch,
  site,
}: {
  widgetId: string;
  currentPage: PageVersion;
  dispatch: any;
  site: any;
}) {
  const deletedWidget = currentPage.content[widgetId];
  if (!deletedWidget?.config?.repeaterWidgetId) return;

  const parentContainerId = deletedWidget.config.parentId;
  const parentContainer = parentContainerId
    ? currentPage.content[parentContainerId]
    : null;
  if (!parentContainer) return;

  const repeaterParentId = parentContainer.config?.parentId;
  const repeaterWidget = repeaterParentId
    ? currentPage.content[repeaterParentId]
    : null;

  if (repeaterWidget?.type !== widgetTypes.RepeaterWidget) return;

  const repeaterWidgetIdTag = deletedWidget.config.repeaterWidgetId;

  repeaterWidget.children.forEach((containerId: string) => {
    if (containerId === parentContainerId) return;
    const container = currentPage.content[containerId];
    if (!container?.children) return;

    const matchingWidget = container.children
      .map(id => currentPage.content[id])
      .find(w => w?.config?.repeaterWidgetId === repeaterWidgetIdTag);

    if (matchingWidget) {
      dispatch(
        removeWidgetAction(site.siteId, currentPage.pageId, matchingWidget.id)
      );
      removeWidgetFromContainer({
        widgetId: matchingWidget.id,
        container,
        currentPage,
        dispatch,
        site,
        pageId: currentPage.pageId,
        repeaterWidgetId: repeaterWidgetIdTag,
      });
    }
  });
}

export function syncRepeaterKeysToNewContainer({
  currentPage,
  templateContainerId,
  newContainerId,
  dispatch,
  site,
  pageId,
  repeaterWidgetId,
}) {
  const templateWidgets = getSortedWidgets(
    currentPage.content[templateContainerId],
    currentPage,
    repeaterWidgetId
  );
  const newWidgets =
    currentPage.content[newContainerId]?.children.map(
      id => currentPage.content[id]
    ) || [];

  templateWidgets.forEach(templateWidget => {
    const newWidget = newWidgets.find(
      w => w.config.repeaterWidgetId === templateWidget.config.repeaterWidgetId
    );
    if (!newWidget) return;

    dispatch(
      updateWidgetConfig(
        site.siteId,
        pageId,
        newWidget.id,
        {
          ...newWidget.config,
          repeaterWidgetKey: templateWidget.config?.repeaterWidgetKey,
          repeaterWidgetOrder: templateWidget.config?.repeaterWidgetOrder,
          repeaterWidgetId: templateWidget.config?.repeaterWidgetId,
        },
        false
      )
    );
  });
}

export function syncLayoutAcrossRepeaterContainers({
  changedWidget,
  currentPage,
  oldCurrentPage,
  site,
  pageId,
  dispatch,
}) {
  const {
    xLocation,
    yLocation,
    width,
    height,
    repeaterWidgetId: widgetId,
  } = changedWidget.config;

  // Find the parent container
  const parentContainerId = changedWidget.config.parentId;
  const parentContainer = currentPage.content[parentContainerId];
  const repeaterWidget = currentPage.content[parentContainer?.config?.parentId];

  if (!repeaterWidget?.children) return;

  const widgetUpdates: Array<{ widgetId: string; config: any }> = [];

  repeaterWidget.children.forEach(containerId => {
    const container = currentPage.content[containerId];
    if (!container || container.id === parentContainerId) return; // skip self

    const targetWidget = container.children
      .map(id => currentPage.content[id])
      .find(w => w.config.repeaterWidgetId === widgetId);

    if (!targetWidget) return;

    const relativeX =
      xLocation - oldCurrentPage.content[changedWidget.id]?.config.xLocation;
    const relativeY =
      yLocation - oldCurrentPage.content[changedWidget.id]?.config.yLocation;

    const newX = targetWidget.config.xLocation + relativeX;
    const newY = targetWidget.config.yLocation + relativeY;

    widgetUpdates.push({
      widgetId: targetWidget.id,
      config: {
        ...targetWidget.config,
        x: newX,
        y: newY,
        xLocation: newX,
        yLocation: newY,
        width: width,
        height: height,
      },
    });
  });

  // Batch all updates into a single undo operation
  if (widgetUpdates.length > 0) {
    dispatch(batchUpdateRepeaterWidgets(site.siteId, pageId, widgetUpdates));
  }
}

export function syncRepeaterLayout({
  changedWidget,
  currentPage,
  oldCurrentPage,
  site,
  pageId,
  dispatch,
}) {
  if (!changedWidget?.config?.parentId) return false;

  const changedParentId = changedWidget.config.parentId;
  const changedParent = currentPage.content[changedParentId];

  // If changed widget is a container (Section), sync other containers
  if (
    changedWidget.type === widgetTypes.Section &&
    currentPage.content[changedWidget.config.parentId]?.type ===
      widgetTypes.RepeaterWidget
  ) {
    const repeaterWidgetId = changedWidget.config.parentId;
    const repeaterWidget = currentPage.content[repeaterWidgetId];

    if (!repeaterWidget?.children) return false;

    const { xLocation, yLocation, width, height } = changedWidget.config;

    const oldContainer = oldCurrentPage.content[changedWidget.id];
    if (!oldContainer) return false;

    const deltaX = xLocation - oldContainer.config.xLocation;
    const deltaY = yLocation - oldContainer.config.yLocation;

    const widgetUpdates: Array<{ widgetId: string; config: any }> = [];

    const layout = repeaterWidget.config?.layout;
    const vSpacing = repeaterWidget.config?.containerVerticalSpacing ?? 23;
    const hSpacing = repeaterWidget.config?.containerHorizontalSpacing ?? 33;
    const itemsCount = repeaterWidget.config?.itemsCount ?? 1;
    const heightChanged = height !== oldContainer.config.height;
    const widthChanged = width !== oldContainer.config.width;
    const sizeChanged = heightChanged || widthChanged;

    // Sort containers by repeaterWidgetIndex for proper positioning
    const sortedContainers = repeaterWidget.children
      .map(id => currentPage.content[id])
      .filter(Boolean)
      .sort(
        (a, b) =>
          (a.config.repeaterWidgetIndex ?? 0) -
          (b.config.repeaterWidgetIndex ?? 0)
      );

    const firstContainer = sortedContainers[0];

    if (layout === 'vertical' && sizeChanged && firstContainer) {
      // Reposition all containers using new height + vSpacing
      sortedContainers.forEach(container => {
        const index = container.config.repeaterWidgetIndex - 1;
        const newY =
          firstContainer.config.yLocation + (height + vSpacing) * index;

        const oldY = container.config.yLocation;
        const childDeltaY = newY - oldY;

        widgetUpdates.push({
          widgetId: container.id,
          config: {
            ...container.config,
            x: container.config.xLocation,
            y: newY,
            xLocation: container.config.xLocation,
            yLocation: newY,
            width: width,
            height: height,
          },
        });

        if (Array.isArray(container.children)) {
          container.children.forEach(childId => {
            const child = currentPage.content[childId];
            if (!child) return;
            widgetUpdates.push({
              widgetId: child.id,
              config: {
                ...child.config,
                x: child.config.xLocation ?? 0,
                y: (child.config.yLocation ?? 0) + childDeltaY,
                xLocation: child.config.xLocation ?? 0,
                yLocation: (child.config.yLocation ?? 0) + childDeltaY,
              },
            });
          });
        }
      });
    } else if (layout === 'horizontal' && sizeChanged && firstContainer) {
      // Reposition containers by row using new dimensions + spacing
      sortedContainers.forEach(container => {
        const index = container.config.repeaterWidgetIndex - 1;
        const positionInRow = index % itemsCount;
        const rowNumber = Math.floor(index / itemsCount);

        const newX =
          firstContainer.config.xLocation + (width + hSpacing) * positionInRow;
        const newY =
          firstContainer.config.yLocation + (height + vSpacing) * rowNumber;

        const oldY = container.config.yLocation;
        const oldX = container.config.xLocation;
        const childDeltaX = newX - oldX;
        const childDeltaY = newY - oldY;

        widgetUpdates.push({
          widgetId: container.id,
          config: {
            ...container.config,
            x: newX,
            y: newY,
            xLocation: newX,
            yLocation: newY,
            width: width,
            height: height,
          },
        });

        if (Array.isArray(container.children)) {
          container.children.forEach(childId => {
            const child = currentPage.content[childId];
            if (!child) return;
            widgetUpdates.push({
              widgetId: child.id,
              config: {
                ...child.config,
                x: (child.config.xLocation ?? 0) + childDeltaX,
                y: (child.config.yLocation ?? 0) + childDeltaY,
                xLocation: (child.config.xLocation ?? 0) + childDeltaX,
                yLocation: (child.config.yLocation ?? 0) + childDeltaY,
              },
            });
          });
        }
      });
    } else {
      // No height change or unknown layout — use simple delta approach
      // Include the changed widget itself in the batch (position already correct)
      widgetUpdates.push({
        widgetId: changedWidget.id,
        config: changedWidget.config,
      });
      repeaterWidget.children.forEach(containerId => {
        if (containerId === changedWidget.id) return;

        const container = currentPage.content[containerId];
        if (!container) return;

        const newX = container.config.xLocation + deltaX;
        const newY = container.config.yLocation + deltaY;

        widgetUpdates.push({
          widgetId: container.id,
          config: {
            ...container.config,
            x: newX,
            y: newY,
            xLocation: newX,
            yLocation: newY,
            width: width,
            height: height,
          },
        });

        if (Array.isArray(container.children)) {
          container.children.forEach(childId => {
            const child = currentPage.content[childId];
            if (!child) return;
            const childNewX = (child.config.xLocation ?? 0) + deltaX;
            const childNewY = (child.config.yLocation ?? 0) + deltaY;
            widgetUpdates.push({
              widgetId: child.id,
              config: {
                ...child.config,
                x: childNewX,
                y: childNewY,
                xLocation: childNewX,
                yLocation: childNewY,
              },
            });
          });
        }
      });
    }

    if (widgetUpdates.length > 0) {
      dispatch(batchUpdateRepeaterWidgets(site?.siteId, pageId, widgetUpdates));
    }

    // Update the repeater widget's own dimensions to fit all containers
    if (sizeChanged && firstContainer && repeaterWidget.config) {
      const containerCount = sortedContainers.length;
      const deltaFromTop =
        firstContainer.config.yLocation - repeaterWidget.config.yLocation;
      const deltaFromLeft =
        firstContainer.config.xLocation - repeaterWidget.config.xLocation;

      let newRepeaterHeight;
      let newRepeaterWidth = repeaterWidget.config.width;

      if (layout === 'vertical') {
        newRepeaterHeight =
          deltaFromTop * 2 +
          height * containerCount +
          vSpacing * (containerCount - 1);
        newRepeaterWidth = deltaFromLeft * 2 + width;
      } else {
        const rowCount = Math.ceil(containerCount / itemsCount);
        const colCount = Math.min(containerCount, itemsCount);
        newRepeaterHeight =
          deltaFromTop * 2 + height * rowCount + vSpacing * (rowCount - 1);
        newRepeaterWidth =
          deltaFromLeft * 2 + width * colCount + hSpacing * (colCount - 1);
      }

      dispatch(
        batchUpdateRepeaterWidgets(site?.siteId, pageId, [
          {
            widgetId: repeaterWidgetId,
            config: {
              ...repeaterWidget.config,
              height: newRepeaterHeight,
              width: newRepeaterWidth,
            },
          },
        ])
      );
    }

    return true;
  }

  // Else, widget is a child inside a container → sync across containers
  const repeaterWidgetId = changedParent?.config?.parentId;
  const repeaterWidget = currentPage.content[repeaterWidgetId];

  if (!repeaterWidget?.children || !changedWidget.config.repeaterWidgetId)
    return false;

  const {
    xLocation,
    yLocation,
    width,
    height,
    repeaterWidgetId: widgetKey,
  } = changedWidget.config;

  const oldWidget = oldCurrentPage.content[changedWidget.id];
  if (!oldWidget) return false;

  const relativeX = xLocation - oldWidget.config.xLocation;
  const relativeY = yLocation - oldWidget.config.yLocation;

  const widgetUpdates: Array<{ widgetId: string; config: any }> = [];

  // Include the original changed widget in the batch
  widgetUpdates.push({
    widgetId: changedWidget.id,
    config: changedWidget.config,
  });

  repeaterWidget.children.forEach(containerId => {
    const container = currentPage.content[containerId];
    if (!container || container.id === changedParentId) return;

    const targetWidget = container.children
      ?.map(id => currentPage.content[id])
      .find(w => w?.config?.repeaterWidgetId === widgetKey);

    if (!targetWidget) return;

    const newX = targetWidget.config.xLocation + relativeX;
    const newY = targetWidget.config.yLocation + relativeY;

    widgetUpdates.push({
      widgetId: targetWidget.id,
      config: {
        ...targetWidget.config,
        x: newX,
        y: newY,
        xLocation: newX,
        yLocation: newY,
        width: width,
        height: height,
      },
    });
  });

  if (widgetUpdates.length > 0) {
    dispatch(batchUpdateRepeaterWidgets(site?.siteId, pageId, widgetUpdates));
  }

  return true;
}

export function getFilteredRepeaterContainers({
  repeaterData,
  containers,
  currentPage,
  filterBy,
}) {
  if (!filterBy || !Array.isArray(repeaterData?.data)) {
    return containers;
  }

  const validIndexes = repeaterData.data.reduce((acc, row, index) => {
    const value = row[filterBy];
    if (value !== undefined && value !== null && value !== '') {
      acc.push(index + 1); // +1 because repeaterWidgetIndex is 1-based
    }
    return acc;
  }, []);

  return containers.filter(containerId => {
    const container = currentPage.content[containerId];
    return validIndexes.includes(container?.config?.repeaterWidgetIndex);
  });
}

export function syncRepeaterConfigChanges({
  changedWidget,
  currentPage,
  site,
  pageId,
  dispatch,
  configChanges,
}: {
  changedWidget: Widget<any>;
  currentPage: PageVersion;
  site: Site;
  pageId: number;
  dispatch: any;
  configChanges: any;
}) {
  if (!changedWidget?.config?.parentId) return false;

  const changedParentId = changedWidget.config.parentId;
  const changedParent = currentPage.content[changedParentId];

  const excludedProperties = [
    'xLocation',
    'yLocation',
    'x',
    'y',
    'emailAddress',
    'url',
    'uploadedFile',
    'selectedPage',
    'linkType',
  ];

  // If changed widget is a container (Section), sync other containers
  if (
    changedWidget.type === widgetTypes.Section &&
    currentPage.content[changedWidget.config.parentId]?.type ===
      widgetTypes.RepeaterWidget
  ) {
    const repeaterWidgetId = changedWidget.config.parentId;
    const repeaterWidget = currentPage.content[repeaterWidgetId];

    if (!repeaterWidget?.children) return false;

    const widgetUpdates: Array<{ widgetId: string; config: any }> = [];

    // Include the original changed widget in the batch
    widgetUpdates.push({
      widgetId: changedWidget.id,
      config: changedWidget.config,
    });

    repeaterWidget.children.forEach(containerId => {
      if (containerId === changedWidget.id) return;

      const container = currentPage.content[containerId];
      if (!container) return;

      // Apply the same config changes to other containers, excluding position-related changes
      const updatedConfig = { ...container.config };

      // Filter out position-related properties to avoid moving containers
      const filteredConfigChanges = Object.keys(configChanges).reduce(
        (acc, key) => {
          if (!excludedProperties.includes(key)) {
            acc[key] = configChanges[key];
          }
          return acc;
        },
        {}
      );

      Object.assign(updatedConfig, filteredConfigChanges);

      widgetUpdates.push({
        widgetId: container.id,
        config: updatedConfig,
      });
    });

    // Batch all container updates into a single undo operation
    if (widgetUpdates.length > 0) {
      dispatch(batchUpdateRepeaterWidgets(site?.siteId, pageId, widgetUpdates));
    }
    return true;
  }

  // Else, widget is a child inside a container
  const repeaterWidgetId = changedParent?.config?.parentId;
  const repeaterWidget = currentPage.content[repeaterWidgetId];

  if (!repeaterWidget?.children || !changedWidget.config.repeaterWidgetId)
    return false;

  const widgetKey = changedWidget.config.repeaterWidgetId;

  const widgetUpdates: Array<{ widgetId: string; config: any }> = [];

  // Include the original changed widget in the batch
  widgetUpdates.push({
    widgetId: changedWidget.id,
    config: changedWidget.config,
  });

  repeaterWidget.children.forEach(containerId => {
    const container = currentPage.content[containerId];
    if (!container || container.id === changedParentId) return;

    const targetWidget = container.children
      ?.map(id => currentPage.content[id])
      .find(w => w?.config?.repeaterWidgetId === widgetKey);

    if (!targetWidget) return;

    // Apply the same config changes to target widget, excluding position-related changes
    const updatedConfig = { ...targetWidget.config };

    // Filtering position-related properties to avoid moving widgets
    const filteredConfigChanges = Object.keys(configChanges).reduce(
      (acc, key) => {
        if (!excludedProperties.includes(key)) {
          acc[key] = configChanges[key];
        }
        return acc;
      },
      {}
    );

    Object.assign(updatedConfig, filteredConfigChanges);

    widgetUpdates.push({
      widgetId: targetWidget.id,
      config: updatedConfig,
    });
  });

  // Batch all child widget updates into a single undo operation
  if (widgetUpdates.length > 0) {
    dispatch(batchUpdateRepeaterWidgets(site?.siteId, pageId, widgetUpdates));
  }

  return true;
}

//adjusting container visibility based on filtering
export async function mapContainersToFilteredRows({
  widgetId,
  currentPage,
  repeaterData,
  filterBy,
  enableFiltering,
  dispatch,
  siteId,
  pageId,
  checkboxFilteredIndexes,
  applyCheckboxFiltering,
  hasSelectedOptions,
}: {
  widgetId: string;
  currentPage: PageVersion;
  repeaterData: any;
  filterBy?: string;
  enableFiltering: boolean;
  dispatch: any;
  siteId: number;
  pageId: number;
  checkboxFilteredIndexes?: number[];
  applyCheckboxFiltering?: boolean;
  hasSelectedOptions?: boolean;
  updateCurrentPage?: (page: PageVersion) => void;
}): Promise<void> {
  const repeaterWidget = currentPage.content[widgetId];
  if (!repeaterWidget || !Array.isArray(repeaterWidget.children)) return;

  const data = repeaterData?.data || [];
  const allContainers = repeaterWidget.children;

  // Get currently visible containers count
  const visibleContainers = allContainers.filter(containerId => {
    const container = currentPage.content[containerId];
    return container?.config?.visible !== false;
  });

  const visibleContainersCount = visibleContainers.length;

  // Sort all containers by repeaterWidgetIndex for consistent ordering
  const sortedContainers = allContainers
    .map(containerId => ({
      id: containerId,
      index:
        currentPage.content[containerId]?.config?.repeaterWidgetIndex ??
        Infinity,
    }))
    .sort((a, b) => a.index - b.index);

  // Case 1: Checkbox filtering with specific indexes requiring more containers
  if (
    applyCheckboxFiltering &&
    checkboxFilteredIndexes &&
    checkboxFilteredIndexes.length > 0 &&
    visibleContainersCount < checkboxFilteredIndexes.length
  ) {
    await handleCheckboxFilteringWithInsufficientContainers({
      checkboxFilteredIndexes,
      allContainers,
      sortedContainers,
      repeaterWidget,
      widgetId,
      currentPage,
      dispatch,
      siteId,
      pageId,
    });
    return;
  }

  // Case 2: Filtering disabled but need to restore containers to match data/containerCount
  if (
    !applyCheckboxFiltering &&
    enableFiltering &&
    (!checkboxFilteredIndexes || checkboxFilteredIndexes.length === 0) &&
    repeaterWidget.config.containerCount > 0 &&
    visibleContainersCount < repeaterWidget.config.containerCount
  ) {
    await handleRestoreContainersForDataLength({
      targetCount: repeaterWidget.config.containerCount,
      allContainers,
      sortedContainers,
      repeaterWidget,
      widgetId,
      currentPage,
      dispatch,
      siteId,
      pageId,
    });
    return;
  }

  // Case 3: Standard filtering logic with visibility management
  await handleStandardFilteringWithVisibility({
    checkboxFilteredIndexes,
    applyCheckboxFiltering,
    hasSelectedOptions,
    enableFiltering,
    filterBy,
    data,
    allContainers,
    sortedContainers,
    repeaterWidget,
    widgetId,
    currentPage,
    dispatch,
    siteId,
    pageId,
  });
}

export const PER_INSTANCE_PROPERTIES = [
  'emailAddress',
  'url',
  'uploadedFile',
  'selectedPage',
  'linkType',
];

type PerInstanceEntry = {
  type: string;
  repeaterWidgetId: any;
  props: Record<string, any>;
};
type PerInstanceSnapshot = Record<number, PerInstanceEntry[]>;

/**
 * Returns true if a per-instance property value is meaningful (not a default/empty value).
 * Default values that should NOT be captured: '', undefined, null, 'None'.
 */
function isPerInstanceValueMeaningful(value: any): boolean {
  if (value === undefined || value === null || value === '') return false;
  if (value === 'None') return false;
  return true;
}

/**
 * Reads ALL per-instance properties from a container's child widgets, including
 * empty/default values (e.g. url:'' or linkType:'None'). Capturing the empty
 * state is what allows a stale link to be cleared when a container is later
 * remapped to a data row that has no link configured. Only children that
 * actually declare per-instance properties are included.
 */
function readAllPerInstanceProps(
  containerId: string,
  currentPage: any
): PerInstanceEntry[] {
  const entries: PerInstanceEntry[] = [];
  const children = currentPage.content?.[containerId]?.children || [];
  children.forEach((wId: string) => {
    const w = currentPage.content?.[wId];
    if (!w) return;
    const captured: Record<string, any> = {};
    let has = false;
    PER_INSTANCE_PROPERTIES.forEach(p => {
      if (p in (w.config || {})) {
        captured[p] = w.config[p];
        has = true;
      }
    });
    if (has) {
      entries.push({
        type: w.type,
        repeaterWidgetId: w.config?.repeaterWidgetId,
        props: captured,
      });
    }
  });
  return entries;
}

/**
 * Builds an object that resets a widget's per-instance properties (url,
 * linkType, etc.) back to their empty/default values. Only currently-meaningful
 * values are included, so an already-empty widget produces `null` and the caller
 * can skip a no-op dispatch. This is used to strip a stale link from a container
 * that has been remapped to a data row which has no link configured.
 */
function buildClearedPerInstanceProps(
  config: Record<string, any> = {}
): Record<string, any> | null {
  const cleared: Record<string, any> = {};
  let changed = false;
  PER_INSTANCE_PROPERTIES.forEach(p => {
    if (p in config && isPerInstanceValueMeaningful(config[p])) {
      cleared[p] = p === 'linkType' ? 'None' : '';
      changed = true;
    }
  });
  return changed ? cleared : null;
}

/**
 * Keeps per-instance widget properties (url, linkType, etc.) aligned with the
 * data row each container displays as the repeater is remapped (filter,
 * paginate, reorder).
 *
 * A per-row store (`config.perInstancePropsSnapshot`, keyed by data row index)
 * is the single source of truth. It is populated once (first realign) or filled
 * for missing rows when returning to the unfiltered baseline, and is NEVER
 * overwritten or discarded thereafter. During active filtering the store is
 * read-only, so the links cannot drift even when the page being read is a render
 * behind (client staleness) or filtering happens repeatedly.
 *
 * Each remapped container is then given its new row's stored props (or has its
 * per-instance values cleared when the row has none). When `widgetId` is
 * provided the updated store is returned so the caller can persist it; for
 * index-based reorders without a `widgetId` the store is transient.
 */
export function realignPerInstanceProperties({
  oldMapping,
  newMapping,
  currentPage,
  dispatch,
  siteId,
  pageId,
  widgetId,
}: {
  oldMapping: Record<string, number>;
  newMapping: Record<string, number>;
  currentPage: any;
  dispatch: any;
  siteId: number;
  pageId: number;
  widgetId?: string;
}) {
  // Build reverse lookup: dataRowIndex → containerId (first owner wins so that a
  // stale/duplicated old mapping cannot flip ownership of a row).
  const dataRowToOldContainer: Record<number, string> = {};
  Object.entries(oldMapping).forEach(([containerId, dataRow]) => {
    if (!(dataRow in dataRowToOldContainer)) {
      dataRowToOldContainer[dataRow] = containerId;
    }
  });

  // Persistent, authoritative store of per-instance props keyed by data row.
  // Loaded from the widget config so a row's link survives filter/paginate
  // cycles where its container is temporarily hidden. It is never discarded.
  const store: PerInstanceSnapshot = widgetId
    ? {
        ...(currentPage.content?.[widgetId]?.config?.perInstancePropsSnapshot ||
          {}),
      }
    : {};
  const hasStore = Object.keys(store).length > 0;

  // Baseline = the unfiltered / fully-restored view: newMapping is the identity
  // permutation (rows 0..n-1 in order). At baseline every container shows its own
  // row, so the live containers can be trusted as the source of truth.
  const newMappingValues = Object.values(newMapping).sort((a, b) => a - b);
  const isBaseline =
    newMappingValues.length > 0 && newMappingValues.every((v, i) => v === i);

  // CAPTURE (populate the store) ONLY for rows that are not already known, and
  // only when it is safe to trust the live containers: on the very first realign
  // (no store yet) or when returning to the unfiltered baseline. Crucially we
  // NEVER overwrite an existing store entry — the store is authoritative, and
  // the page we read may be stale (a render behind on the client). Re-capturing
  // an already-known row from a stale container is exactly what caused links to
  // collapse to a wrong/duplicate url under repeated filtering. Capture is keyed
  // by the row each VISIBLE container currently displays (oldMapping) and records
  // FULL per-instance props, including empty values so a "no link" state is kept.
  if (!hasStore || isBaseline) {
    Object.entries(oldMapping).forEach(([containerId, dataRow]) => {
      if (dataRow in store) return; // never overwrite an authoritative entry
      const container = currentPage.content?.[containerId];
      if (!container) return;
      if (container.config?.visible === false) return; // trust only visible containers
      const entries = readAllPerInstanceProps(containerId, currentPage);
      if (entries.length > 0) {
        store[dataRow] = entries;
      }
    });
  }

  // For any needed row still missing from the store (e.g. the very first realign
  // before a store exists, or a row whose owner is hidden), fall back to reading
  // the container that previously owned it.
  const neededRows = new Set<number>(Object.values(newMapping));
  neededRows.forEach((dataRow: number) => {
    if (store[dataRow]) return;
    const srcContainerId = dataRowToOldContainer[dataRow];
    if (srcContainerId && currentPage.content?.[srcContainerId]) {
      const entries = readAllPerInstanceProps(srcContainerId, currentPage);
      if (entries.length > 0) {
        store[dataRow] = entries;
      }
    }
  });

  // APPLY: give each remapped container the per-instance props for the row it
  // will now display. If the row has no stored props, clear any stale values so
  // the container never keeps a link that belonged to a different row.
  //
  // The "already owns this row" skip must be decided from the REAL mapping (what
  // each container actually displays right now) — NOT from `oldMapping`, which
  // may contain positional GUESSES from `buildCompleteOldMapping` for containers
  // that are not in the active mapping (common under pagination, where the config
  // mapping only holds the current page's containers). Trusting a positional
  // guess here would skip a needed link write and leave a stale/wrong link on a
  // container that was never actually placed on that row.
  const realMapping: Record<string, number> = widgetId
    ? currentPage.content?.[widgetId]?.config?.repeaterDataMapping || {}
    : oldMapping;

  Object.entries(newMapping).forEach(([destContainerId, dataRow]) => {
    // Skip only when the container genuinely already displays this row.
    if (realMapping[destContainerId] === dataRow) return;

    const propsForRow = store[dataRow];

    const destChildren = currentPage.content?.[destContainerId]?.children || [];
    destChildren.forEach((wId: string) => {
      const w = currentPage.content?.[wId];
      if (!w) return;

      // Only per-instance-capable children participate in realignment.
      const hasPerInstanceProp = PER_INSTANCE_PROPERTIES.some(
        p => p in (w.config || {})
      );
      if (!hasPerInstanceProp) return;

      const match = propsForRow?.find(
        s =>
          s.type === w.type && s.repeaterWidgetId === w.config?.repeaterWidgetId
      );

      if (match) {
        // Always write the authoritative per-instance props. We deliberately do
        // NOT skip based on the container's current value: the page we read can
        // be stale (a render behind on the client), so trusting it to decide
        // "no change needed" would leave a wrong link in place.
        dispatch(
          updateWidgetConfig(
            siteId,
            pageId,
            wId,
            { ...w.config, ...match.props },
            false
          )
        );
      } else {
        // The new data row has no stored props at all (its owner was never
        // captured). As a fallback, strip any stale per-instance values so the
        // container does not keep the link from the row it previously displayed.
        const cleared = buildClearedPerInstanceProps(w.config);
        if (cleared) {
          dispatch(
            updateWidgetConfig(
              siteId,
              pageId,
              wId,
              { ...w.config, ...cleared },
              false
            )
          );
        }
      }
    });
  });

  // Persist the authoritative store so hidden rows survive future operations.
  // (For index-based reorders without a widgetId the store is transient.)
  return widgetId ? store : undefined;
}

/**
 * Convenience wrapper for index-based permutations (moveRow, sort).
 * Converts a newDataIndex → oldDataIndex mapping into container-based
 * oldMapping/newMapping and delegates to realignPerInstanceProperties.
 */
export function realignPerInstancePropertiesByIndex({
  newToOld,
  repeaterDataMapping,
  currentPage,
  dispatch,
  siteId,
  pageId,
  widgetId,
}: {
  newToOld: Record<number, number>;
  repeaterDataMapping: Record<string, number>;
  currentPage: any;
  dispatch: any;
  siteId: number;
  pageId: number;
  widgetId?: string;
}) {
  // Build index → containerId from current mapping
  const indexToContainer: Record<number, string> = {};
  Object.entries(repeaterDataMapping).forEach(([containerId, idx]) => {
    indexToContainer[idx] = containerId;
  });

  // Construct newMapping: for each affected index, the container there
  // should now get the per-instance props from the old index's container
  const oldMapping = { ...repeaterDataMapping };
  const newMapping: Record<string, number> = {};
  Object.entries(newToOld).forEach(([newIdxStr, oldIdx]) => {
    const newIdx = parseInt(newIdxStr);
    const destContainerId = indexToContainer[newIdx];
    if (destContainerId) {
      newMapping[destContainerId] = oldIdx;
    }
  });

  // Keep unaffected containers in the mapping as-is
  Object.entries(repeaterDataMapping).forEach(([containerId, idx]) => {
    if (!(containerId in newMapping)) {
      newMapping[containerId] = idx;
    }
  });

  // Realign the live container props for the permutation. This intentionally
  // runs WITHOUT widgetId so it captures from the (all-visible) live containers
  // rather than the stored snapshot — the containers are the source of truth
  // during a data-table reorder.
  realignPerInstanceProperties({
    oldMapping,
    newMapping,
    currentPage,
    dispatch,
    siteId,
    pageId,
  });

  // Reordering the data (moveRow/sort) permutes the data rows, so the persisted
  // per-row snapshot must be permuted in lockstep. We RETURN the permuted
  // snapshot rather than dispatching it here: the caller folds it into the SAME
  // config update that writes the reordered repeaterData. Dispatching a separate
  // config update from this function would spread a stale (render-time) config
  // and clobber the freshly reordered repeaterData. Reads are taken from the
  // original snapshot so a swap cannot corrupt itself.
  if (widgetId) {
    const existingSnapshot =
      currentPage.content?.[widgetId]?.config?.perInstancePropsSnapshot;
    if (existingSnapshot && Object.keys(existingSnapshot).length > 0) {
      const permutedSnapshot: Record<number, any> = { ...existingSnapshot };
      Object.entries(newToOld).forEach(([newIdxStr, oldIdx]) => {
        const newIdx = parseInt(newIdxStr);
        if (oldIdx in existingSnapshot) {
          permutedSnapshot[newIdx] = existingSnapshot[oldIdx];
        } else {
          delete permutedSnapshot[newIdx];
        }
      });
      return permutedSnapshot;
    }
  }
  return undefined;
}

/**
 * Builds a complete mapping that includes ALL containers, not just those in the
 * active repeaterDataMapping. Containers not in the active mapping get their
 * sorted positional index as a fallback. This ensures the snapshot captures
 * per-instance props from ALL containers including those beyond the visible count.
 */
function buildCompleteOldMapping(
  activeMapping: Record<string, number>,
  sortedContainers: Array<{ id: string; index: number }>
): Record<string, number> {
  const complete: Record<string, number> = { ...activeMapping };
  const usedRows = new Set<number>(Object.values(activeMapping));

  sortedContainers.forEach((container, positionalIndex) => {
    if (!(container.id in complete) && !usedRows.has(positionalIndex)) {
      complete[container.id] = positionalIndex;
      usedRows.add(positionalIndex);
    }
  });
  return complete;
}

async function handleCheckboxFilteringWithInsufficientContainers({
  checkboxFilteredIndexes,
  sortedContainers,
  repeaterWidget,
  widgetId,
  currentPage,
  dispatch,
  siteId,
  pageId,
}: {
  checkboxFilteredIndexes: number[];
  allContainers: string[];
  sortedContainers: Array<{ id: string; index: number }>;
  repeaterWidget: any;
  widgetId: string;
  currentPage: any;
  dispatch: any;
  siteId: number;
  pageId: number;
}): Promise<void> {
  const requiredContainers = checkboxFilteredIndexes.length;

  // Update visibility and mapping for checkbox filtering
  const mapping = {};

  sortedContainers.forEach((container, index) => {
    const shouldBeVisible = index < requiredContainers;

    if (shouldBeVisible) {
      mapping[container.id] = checkboxFilteredIndexes[index];
    }

    updateContainerAndChildrenVisibility(
      container.id,
      shouldBeVisible,
      currentPage,
      dispatch,
      siteId,
      pageId
    );
  });

  // Move per-instance properties so they follow the data rows
  const snapshot = realignPerInstanceProperties({
    oldMapping: buildCompleteOldMapping(
      repeaterWidget.config?.repeaterDataMapping || {},
      sortedContainers
    ),
    newMapping: mapping,
    currentPage,
    dispatch,
    siteId,
    pageId,
    widgetId,
  });

  // Update widget config with new mapping
  dispatch(
    updateWidgetConfig(
      siteId,
      pageId,
      widgetId,
      {
        ...repeaterWidget.config,
        repeaterDataMapping: mapping,
        perInstancePropsSnapshot: snapshot,
      },
      false
    )
  );
}

async function handleRestoreContainersForDataLength({
  targetCount,
  allContainers,
  sortedContainers,
  repeaterWidget,
  widgetId,
  currentPage,
  dispatch,
  siteId,
  pageId,
}: {
  targetCount: number;
  allContainers: string[];
  sortedContainers: Array<{ id: string; index: number }>;
  repeaterWidget: any;
  widgetId: string;
  currentPage: any;
  dispatch: any;
  siteId: number;
  pageId: number;
}): Promise<void> {
  const totalContainers = allContainers.length;

  // Create additional containers if needed
  if (totalContainers < targetCount) {
    await createAdditionalContainers({
      startIndex: totalContainers,
      endIndex: targetCount,
      repeaterWidget,
      widgetId,
      currentPage,
      dispatch,
      siteId,
      pageId,
    });
  }

  // Show containers up to targetCount and create mapping
  const mapping = {};

  sortedContainers.forEach((container, index) => {
    const shouldBeVisible = index < targetCount;

    if (shouldBeVisible) {
      mapping[container.id] = index;
    }

    updateContainerAndChildrenVisibility(
      container.id,
      shouldBeVisible,
      currentPage,
      dispatch,
      siteId,
      pageId
    );
  });

  // Move per-instance properties so they follow the data rows
  const snapshot = realignPerInstanceProperties({
    oldMapping: buildCompleteOldMapping(
      repeaterWidget.config?.repeaterDataMapping || {},
      sortedContainers
    ),
    newMapping: mapping,
    currentPage,
    dispatch,
    siteId,
    pageId,
    widgetId,
  });

  // Update widget config
  dispatch(
    updateWidgetConfig(
      siteId,
      pageId,
      widgetId,
      {
        ...repeaterWidget.config,
        repeaterDataMapping: mapping,
        perInstancePropsSnapshot: snapshot,
      },
      false
    )
  );
}

async function handleStandardFilteringWithVisibility({
  checkboxFilteredIndexes,
  applyCheckboxFiltering,
  hasSelectedOptions,
  enableFiltering,
  filterBy,
  data,
  allContainers,
  sortedContainers,
  repeaterWidget,
  widgetId,
  currentPage,
  dispatch,
  siteId,
  pageId,
}: {
  checkboxFilteredIndexes?: number[];
  applyCheckboxFiltering?: boolean;
  hasSelectedOptions?: boolean;
  enableFiltering: boolean;
  filterBy?: string;
  data: any[];
  allContainers: string[];
  sortedContainers: Array<{ id: string; index: number }>;
  repeaterWidget: any;
  widgetId: string;
  currentPage: any;
  dispatch: any;
  siteId: number;
  pageId: number;
}): Promise<void> {
  // Compute filtered row indexes based on filtering type
  let filteredIndexes: number[] = [];

  if (applyCheckboxFiltering) {
    filteredIndexes = hasSelectedOptions
      ? checkboxFilteredIndexes || []
      : data.map((_, index) => index);
  } else if (enableFiltering && filterBy) {
    filteredIndexes = data.reduce((acc: number[], row: any, index: number) => {
      const value = row[filterBy];
      if (value !== undefined && value !== null && value !== '') {
        acc.push(index);
      }
      return acc;
    }, []);
  } else {
    // Show all containers when no filtering is applied
    filteredIndexes = data.map((_, index) => index);
  }

  const requiredContainers = filteredIndexes.length;
  const totalContainers = allContainers.length;

  // Create additional containers if needed for non-filtering scenarios
  if (!enableFiltering && totalContainers < requiredContainers) {
    await createAdditionalContainers({
      startIndex: totalContainers,
      endIndex: requiredContainers,
      repeaterWidget,
      widgetId,
      currentPage,
      dispatch,
      siteId,
      pageId,
    });
  }

  // Update visibility and create mapping
  const mapping: Record<string, number> = {};

  sortedContainers.forEach((container, index) => {
    const shouldBeVisible = index < requiredContainers;

    if (shouldBeVisible) {
      mapping[container.id] = filteredIndexes[index];
    }

    updateContainerAndChildrenVisibility(
      container.id,
      shouldBeVisible,
      currentPage,
      dispatch,
      siteId,
      pageId
    );
  });

  // Move per-instance properties so they follow the data rows
  const snapshot = realignPerInstanceProperties({
    oldMapping: buildCompleteOldMapping(
      repeaterWidget.config?.repeaterDataMapping || {},
      sortedContainers
    ),
    newMapping: mapping,
    currentPage,
    dispatch,
    siteId,
    pageId,
    widgetId,
  });

  // Update widget config
  dispatch(
    updateWidgetConfig(
      siteId,
      pageId,
      widgetId,
      {
        ...repeaterWidget.config,
        repeaterDataMapping: mapping,
        perInstancePropsSnapshot: snapshot,
      },
      false
    )
  );
}

async function createAdditionalContainers({
  startIndex,
  endIndex,
  repeaterWidget,
  widgetId,
  currentPage,
  dispatch,
  siteId,
  pageId,
}: {
  startIndex: number;
  endIndex: number;
  repeaterWidget: any;
  widgetId: string;
  currentPage: any;
  dispatch: any;
  siteId: number;
  pageId: number;
}): Promise<void> {
  const initialWidgetConfig = getInitialChildren(widgetTypes.RepeaterWidget);
  const sectionChildWidgetConfigs = getInitialChildren(
    'RepeaterSectionChildren'
  );

  // Collect template repeaterWidgetIds so we can identify manually-added children
  const templateRepeaterWidgetIds = new Set<number>();
  sectionChildWidgetConfigs.forEach((sectionChildren: any) => {
    const children = Array.isArray(sectionChildren)
      ? sectionChildren
      : [sectionChildren];
    children.forEach((child: any) => {
      if (child.repeaterWidgetId != null) {
        templateRepeaterWidgetIds.add(child.repeaterWidgetId);
      }
    });
  });

  // Find first existing container to use as reference for extra children
  // and copying visual attributes (height, background, border, etc.)
  const referenceContainerId = repeaterWidget.children?.[0] || null;
  const referenceConfig = referenceContainerId
    ? currentPage?.content[referenceContainerId]?.config
    : null;

  // Build child widget configs from the reference container's actual state
  // so that updated layouts (position/size changes) are preserved in new containers
  let effectiveChildConfigs = sectionChildWidgetConfigs;
  let referenceSectionConfig: { x: number; y: number } | null = null;
  if (referenceContainerId && currentPage?.content[referenceContainerId]) {
    const refChildren =
      currentPage.content[referenceContainerId].children || [];
    const templateChildren = refChildren
      .map((childId: string) => {
        const childContent = currentPage?.content[childId];
        if (!childContent) return null;
        return {
          id: childId,
          type: childContent.type,
          config: childContent.config,
        };
      })
      .filter(
        (child: any) => child != null && child.config?.repeaterWidgetId != null
      );

    if (templateChildren.length > 0) {
      referenceSectionConfig = {
        x:
          referenceConfig?.xLocation ??
          referenceConfig?.x ??
          initialWidgetConfig[0].x,
        y:
          referenceConfig?.yLocation ??
          referenceConfig?.y ??
          initialWidgetConfig[0].y,
      };
      // Include the full reference config so child widgets inherit all updated properties
      // but exclude per-instance properties that should not carry over to new containers
      const perInstanceProperties = [
        'emailAddress',
        'url',
        'uploadedFile',
        'selectedPage',
        'linkType',
      ];
      const referenceChildArray = templateChildren.map((child: any) => {
        const filteredConfig = { ...child.config };
        perInstanceProperties.forEach(prop => delete filteredConfig[prop]);
        return {
          widgetType: child.type,
          x: child.config.xLocation ?? child.config.x,
          y: child.config.yLocation ?? child.config.y,
          width: child.config.width,
          height: child.config.height,
          minWidth: child.config.minWidth,
          minHeight: child.config.minHeight,
          repeaterWidgetId: child.config.repeaterWidgetId,
          repeaterWidgetKey: child.config.repeaterWidgetKey,
          tabIndex: child.config.tabIndex ?? 0,
          referenceConfig: filteredConfig,
        };
      });
      effectiveChildConfigs = initialWidgetConfig.map(
        () => referenceChildArray
      );
    }
  }

  for (let i = startIndex; i < endIndex; i++) {
    const sectionConfig = initialWidgetConfig[i % 3];
    const rowNumber = Math.ceil((i + 1) / 3);

    const newContainerConfig = createContainerConfig(
      repeaterWidget,
      sectionConfig,
      rowNumber,
      widgetId,
      i + 1,
      referenceConfig
    );

    const newContainerId = uuidv4();

    // Add new container
    dispatch(
      addChild(
        siteId,
        pageId,
        widgetId,
        sectionConfig?.widgetType,
        newContainerConfig,
        0,
        newContainerId,
        sectionConfig?.tabIndex
      )
    );

    // Add template child widgets to the new container
    const childSectionOrigin = referenceSectionConfig || sectionConfig;
    if (effectiveChildConfigs[i % 3]) {
      effectiveChildConfigs[i % 3].forEach(childWidget => {
        createChildWidget({
          childWidget,
          repeaterWidget,
          newContainerConfig,
          sectionConfig: childSectionOrigin,
          newContainerId,
          siteId,
          pageId,
          dispatch,
        });
      });
    }

    // Clone any manually-added children from the reference container
    if (referenceContainerId && currentPage) {
      cloneExtraChildrenFromReference({
        referenceContainerId,
        newContainerId,
        newContainerConfig,
        templateRepeaterWidgetIds,
        currentPage,
        siteId,
        pageId,
        dispatch,
      });
    }
  }
}

function updateContainerAndChildrenVisibility(
  containerId: string,
  shouldBeVisible: boolean,
  currentPage: any,
  dispatch: any,
  siteId: number,
  pageId: number
): void {
  const container = currentPage.content[containerId];
  if (!container) return;

  // Update container visibility only if it has changed
  const currentVisibility = container.config.visible !== false;
  if (currentVisibility !== shouldBeVisible) {
    dispatch(
      updateWidgetConfig(
        siteId,
        pageId,
        containerId,
        {
          ...container.config,
          visible: shouldBeVisible,
        },
        false
      )
    );
  }

  // Update children visibility
  if (container.children && Array.isArray(container.children)) {
    container.children.forEach((childId: string) => {
      const child = currentPage.content[childId];
      if (child) {
        const childCurrentVisibility = child.config.visible !== false;
        if (childCurrentVisibility !== shouldBeVisible) {
          dispatch(
            updateWidgetConfig(
              siteId,
              pageId,
              childId,
              {
                ...child.config,
                visible: shouldBeVisible,
              },
              false
            )
          );
        }
      }
    });
  }
}

function createContainerConfig(
  repeaterWidget: any,
  sectionConfig: any,
  rowNumber: number,
  widgetId: string,
  repeaterWidgetIndex: number,
  referenceConfig?: any
): any {
  const widget = getWidgetLazy(sectionConfig?.widgetType, true);
  const baseConfig = referenceConfig || widget.initialConfig;
  const refHeight = referenceConfig?.height ?? sectionConfig?.height;

  return {
    ...baseConfig,
    visible: true,
    xLocation: repeaterWidget.config.xLocation + sectionConfig?.x,
    yLocation:
      repeaterWidget.config.yLocation +
      sectionConfig?.y * rowNumber +
      refHeight * (rowNumber - 1),
    x: repeaterWidget.config.xLocation + sectionConfig?.x,
    y:
      repeaterWidget.config.yLocation +
      sectionConfig?.y * rowNumber +
      refHeight * (rowNumber - 1),
    width: referenceConfig?.width ?? sectionConfig?.width,
    height: refHeight,
    minWidth: referenceConfig?.minWidth ?? sectionConfig?.minWidth,
    minHeight: referenceConfig?.minHeight ?? sectionConfig?.minHeight,
    parentId: widgetId,
    repeaterWidgetIndex,
  };
}

function createChildWidget({
  childWidget,
  repeaterWidget,
  newContainerConfig,
  sectionConfig,
  newContainerId,
  siteId,
  pageId,
  dispatch,
}: {
  childWidget: any;
  repeaterWidget: any;
  newContainerConfig: any;
  sectionConfig: any;
  newContainerId: string;
  siteId: number;
  pageId: number;
  dispatch: any;
}): void {
  const widget = getWidgetLazy(childWidget.widgetType, true);

  // Use the reference child's full config as base when available,
  // otherwise fall back to the widget type's default initialConfig
  const baseConfig = childWidget.referenceConfig || widget.initialConfig;

  const defaultChildConfig = {
    ...baseConfig,
    visible: true,
    xLocation: repeaterWidget.config.xLocation + childWidget.x,
    yLocation: newContainerConfig.y + (childWidget.y - sectionConfig.y),
    x: repeaterWidget.config.xLocation + childWidget.x,
    y: newContainerConfig.y + (childWidget.y - sectionConfig.y),
    width: childWidget.width,
    height: childWidget.height,
    minWidth: childWidget.minWidth,
    minHeight: childWidget.minHeight,
    parentId: newContainerId,
    repeaterWidgetKey:
      repeaterWidget.config.widgetDataConnections?.[
        childWidget.repeaterWidgetId
      ] ?? childWidget.repeaterWidgetKey,
    repeaterWidgetId: childWidget.repeaterWidgetId,
  };

  const widgetId = uuidv4();

  dispatch(
    addChild(
      siteId,
      pageId,
      newContainerId,
      childWidget.widgetType,
      defaultChildConfig,
      0,
      widgetId,
      childWidget.tabIndex
    )
  );
}

/**
 * Clones manually-added child widgets from a reference container into a newly
 * created container. Only children whose repeaterWidgetId is NOT in the
 * template set are cloned, so template children are not duplicated.
 * @param referenceContainerId - ID of an existing container to clone from
 * @param newContainerId - ID of the newly created container
 * @param newContainerConfig - config of the new container (used for position offset)
 * @param templateRepeaterWidgetIds - Set of repeaterWidgetId values that come from the template
 * @param currentPage - current page version with content
 * @param siteId - site ID
 * @param pageId - page ID
 * @param dispatch - Redux dispatch
 */
export function cloneExtraChildrenFromReference({
  referenceContainerId,
  newContainerId,
  newContainerConfig,
  templateRepeaterWidgetIds,
  currentPage,
  siteId,
  pageId,
  dispatch,
}: {
  referenceContainerId: string;
  newContainerId: string;
  newContainerConfig: any;
  templateRepeaterWidgetIds: Set<number>;
  currentPage: any;
  siteId: number;
  pageId: number;
  dispatch: any;
}): void {
  const refContainer = currentPage.content[referenceContainerId];
  if (!refContainer?.children) return;

  const refContainerConfig = refContainer.config;

  refContainer.children.forEach((childId: string) => {
    const child = currentPage.content[childId];
    if (!child) return;

    const rwId = child.config?.repeaterWidgetId;
    // Only clone children that were added manually (not part of the template)
    if (rwId == null || templateRepeaterWidgetIds.has(rwId)) return;

    // Calculate position relative to the reference container, then offset to the new container
    const relativeX = child.config.xLocation - refContainerConfig.xLocation;
    const relativeY = child.config.yLocation - refContainerConfig.yLocation;

    const targetX = newContainerConfig.xLocation + relativeX;
    const targetY = newContainerConfig.yLocation + relativeY;

    const clonedConfig = {
      ...child.config,
      parentId: newContainerId,
      xLocation: targetX,
      yLocation: targetY,
      x: targetX,
      y: targetY,
    };

    // Strip per-instance properties that should not carry over to cloned widgets
    const perInstanceProps = [
      'emailAddress',
      'url',
      'uploadedFile',
      'selectedPage',
      'linkType',
    ];
    perInstanceProps.forEach(prop => delete clonedConfig[prop]);

    const newWidgetId = uuidv4();

    dispatch(
      addChild(
        siteId,
        pageId,
        newContainerId,
        child.type,
        clonedConfig,
        0,
        newWidgetId,
        child.tabIndex ?? -1
      )
    );
  });
}

/*
 * Get filtered containers based on checkbox selections and their corresponding data indices.
 */
export const getFilteredContainersAndIndices = (
  repeaterData: any,
  filterBy: string,
  selectedOptions: string[],
  allContainers: string[],
  currentPage: any
): { filteredContainerIds: string[]; filteredDataIndices: number[] } => {
  if (!filterBy || !repeaterData?.data || !selectedOptions) {
    // Return all containers and indices if no filtering
    const allIndices = repeaterData?.data?.map((_, index) => index) || [];
    return {
      filteredContainerIds: allContainers,
      filteredDataIndices: allIndices,
    };
  }

  if (selectedOptions.length === 0) {
    const allIndices = repeaterData?.data?.map((_, index) => index) || [];
    return {
      filteredContainerIds: allContainers,
      filteredDataIndices: allIndices,
    };
  }

  const filteredDataIndices: number[] = [];
  repeaterData.data.forEach((row, index) => {
    if (rowMatchesSelectedTags(row[filterBy], selectedOptions)) {
      filteredDataIndices.push(index);
    }
  });

  // Sort containers by their repeaterWidgetIndex
  const sortedContainers = allContainers
    .map(containerId => ({
      id: containerId,
      index:
        currentPage.content[containerId]?.config?.repeaterWidgetIndex ??
        Infinity,
    }))
    .sort((a, b) => a.index - b.index);

  const filteredContainerIds = sortedContainers
    .slice(0, filteredDataIndices.length)
    .map(c => c.id);

  return { filteredContainerIds, filteredDataIndices };
};

export function getVisibleContainersCount(
  containers: string[],
  currentPage: any
): number {
  return containers.filter(containerId => {
    const container = currentPage.content[containerId];
    return container?.config?.visible !== false;
  }).length;
}

export function getVisibleContainers(
  containers: string[],
  currentPage: any
): string[] {
  return containers.filter(containerId => {
    const container = currentPage.content[containerId];
    return container?.config?.visible !== false;
  });
}

export const updateContainerVisibility = (
  allContainers: string[],
  visibleContainers: string[],
  currentPage: any,
  dispatch: any,
  siteId: number,
  pageId: number
): void => {
  allContainers.forEach(containerId => {
    const container = currentPage.content[containerId];
    if (!container) return;

    const shouldBeVisible = visibleContainers.includes(containerId);
    const isCurrentlyVisible = container.config.visible !== false;

    if (shouldBeVisible !== isCurrentlyVisible) {
      dispatch(
        updateWidgetConfig(
          siteId,
          pageId,
          containerId,
          {
            ...container.config,
            visible: shouldBeVisible,
          },
          false
        )
      );
    }
  });
};

export const mapContainersToPaginatedRows = async (
  widgetId: string,
  enablePagination: boolean,
  currentPage: any,
  repeaterData: any,
  paginatedItemsPerPage: number,
  dispatch: any,
  siteId: number,
  pageId: number,
  pageNumber: number,
  filteredContainerIds?: string[], // Optional: if provided, pagination works only on these filtered containers
  filteredDataIndices?: number[] // Optional: corresponding data indices for filtered containers
): Promise<void> => {
  const repeaterWidget = currentPage.content[widgetId];
  if (!repeaterWidget || !Array.isArray(repeaterWidget.children)) return;

  const allContainers = repeaterWidget.children;
  const data = repeaterData?.data || [];

  // Determining which containers to work with (filtered or all)
  const workingContainerIds = filteredContainerIds || allContainers;
  const workingDataIndices =
    filteredDataIndices || data.map((_, index) => index);

  const sortedContainers = workingContainerIds
    .map(containerId => ({
      id: containerId,
      index:
        currentPage.content[containerId]?.config?.repeaterWidgetIndex ??
        Infinity,
    }))
    .sort((a, b) => a.index - b.index);

  // Sorted list of ALL containers (not just the working/filtered subset) used to
  // build the baseline old mapping for per-instance property realignment.
  const sortedAllContainers = allContainers
    .map((containerId: string) => ({
      id: containerId,
      index:
        currentPage.content[containerId]?.config?.repeaterWidgetIndex ??
        Infinity,
    }))
    .sort((a, b) => a.index - b.index);

  if (!enablePagination) {
    const mapping = {};
    sortedContainers.forEach((container, index) => {
      mapping[container.id] = workingDataIndices[index];
    });

    // Update container/child visibility FIRST so the per-instance realignment
    // below is the LAST writer of the child configs. Otherwise the visibility
    // update (which spreads the child's current config) would clobber the
    // url/linkType that realignment just wrote. This mirrors the filtering path.
    sortedContainers.forEach(container =>
      updateContainerAndChildrenVisibility(
        container.id,
        true,
        currentPage,
        dispatch,
        siteId,
        pageId
      )
    );

    // Move per-instance properties (url, linkType, etc.) so they follow the data
    // rows into their newly mapped containers, mirroring the filtering path.
    const snapshot = realignPerInstanceProperties({
      oldMapping: buildCompleteOldMapping(
        repeaterWidget.config?.repeaterDataMapping || {},
        sortedAllContainers
      ),
      newMapping: mapping,
      currentPage,
      dispatch,
      siteId,
      pageId,
      widgetId,
    });

    dispatch(
      updateWidgetConfig(
        siteId,
        pageId,
        widgetId,
        {
          ...repeaterWidget.config,
          repeaterDataMapping: mapping,
          perInstancePropsSnapshot: snapshot,
          enablePagination: false,
        },
        false
      )
    );
    return;
  }

  // Calculate pagination bounds based on filtered data
  const totalFilteredItems = workingDataIndices.length;
  const startIndex = (pageNumber - 1) * paginatedItemsPerPage;
  const endIndex = Math.min(
    startIndex + paginatedItemsPerPage,
    totalFilteredItems
  );
  const itemsOnCurrentPage = endIndex - startIndex;

  const containersForCurrentPage = sortedContainers.slice(
    0,
    itemsOnCurrentPage
  );

  // Create mapping: Map the first N containers to the current page's data indices
  const mapping = {};
  containersForCurrentPage.forEach((container, index) => {
    const dataIndex = workingDataIndices[startIndex + index];
    mapping[container.id] = dataIndex;
  });

  // Update container/child visibility FIRST so the per-instance realignment
  // below is the LAST writer of the child configs. Otherwise the visibility
  // update (which spreads the child's current config) would clobber the
  // url/linkType that realignment just wrote — this was the root cause of a
  // single container mis-redirecting under pagination. Mirrors the filtering path.
  allContainers.forEach(containerId => {
    const shouldBeVisible = containersForCurrentPage.some(
      c => c.id === containerId
    );
    updateContainerAndChildrenVisibility(
      containerId,
      shouldBeVisible,
      currentPage,
      dispatch,
      siteId,
      pageId
    );
  });

  // Move per-instance properties (url, linkType, etc.) so they follow the data
  // rows into their newly mapped containers. Without this, containers keep the
  // link that belonged to whatever row they previously displayed, which sends
  // users to the wrong URL after filtering/pagination remaps the containers.
  const snapshot = realignPerInstanceProperties({
    oldMapping: buildCompleteOldMapping(
      repeaterWidget.config?.repeaterDataMapping || {},
      sortedAllContainers
    ),
    newMapping: mapping,
    currentPage,
    dispatch,
    siteId,
    pageId,
    widgetId,
  });

  dispatch(
    updateWidgetConfig(
      siteId,
      pageId,
      widgetId,
      {
        ...repeaterWidget.config,
        repeaterDataMapping: mapping,
        perInstancePropsSnapshot: snapshot,
        enablePagination: enablePagination,
        paginatedItemsPerPage: paginatedItemsPerPage,
        currentPaginationPage: pageNumber,
      },
      false
    )
  );
};

export function syncWidgetToAllRepeaterContainers({
  addedWidget,
  currentPage,
  siteId,
  pageId,
  dispatch,
}: SyncWidgetAddParams) {
  const parentContainerId = addedWidget.config.parentId;
  const parentContainer = currentPage.content[parentContainerId];

  if (!parentContainer) return;

  const repeaterWidgetId = parentContainer.config.parentId;
  const repeaterWidget = currentPage.content[repeaterWidgetId];

  if (!repeaterWidget || !repeaterWidget.children) return;

  const { xLocation, yLocation } = addedWidget.config;

  repeaterWidget.children.forEach(containerId => {
    if (containerId === parentContainerId) return;

    const container = currentPage.content[containerId];
    if (!container) return;

    const newWidgetId = uuidv4();

    const relativeX = xLocation - parentContainer.config.xLocation;
    const relativeY = yLocation - parentContainer.config.yLocation;

    const targetX = container.config.xLocation + relativeX;
    const targetY = container.config.yLocation + relativeY;

    const newWidgetConfig = {
      ...addedWidget.config,
      parentId: containerId,
      x: targetX,
      y: targetY,
      xLocation: targetX,
      yLocation: targetY,
    };

    dispatch(
      addChild(
        siteId,
        pageId,
        containerId,
        addedWidget.type,
        newWidgetConfig,
        0,
        newWidgetId,
        addedWidget.tabIndex
      )
    );
  });
}

/**
 * When a child widget is added to a repeater container (via grouping or
 * duplication), this function assigns repeater metadata (repeaterWidgetId,
 * repeaterWidgetOrder) to the source widget and creates matching copies in
 * all sibling containers, keeping the repeater in sync.
 *
 * @param addedWidget - The widget that was just added to a container
 * @param currentPage - The current page version (with updated content reflecting the add)
 * @param siteId - The current site ID
 * @param pageId - The current page ID
 * @param dispatch - Redux dispatch function
 */
export function syncAddAcrossRepeaterContainers({
  addedWidget,
  currentPage,
  siteId,
  pageId,
  dispatch,
}: SyncWidgetAddParams) {
  const parentContainerId = addedWidget.config.parentId;
  const parentContainer = currentPage.content[parentContainerId];

  if (!parentContainer) return;

  const repeaterParentId = parentContainer.config.parentId;
  const repeaterWidget = currentPage.content[repeaterParentId];

  // Only sync if the parent's parent is a RepeaterWidget
  if (
    !repeaterWidget ||
    repeaterWidget.type !== widgetTypes.RepeaterWidget ||
    !repeaterWidget.children
  )
    return;

  // Calculate the next repeaterWidgetId across all containers
  let maxRepeaterWidgetId = 0;
  repeaterWidget.children.forEach((containerId: string) => {
    const container = currentPage.content[containerId];
    if (!container?.children) return;
    container.children.forEach((childId: string) => {
      const child = currentPage.content[childId];
      const rwId = child?.config?.repeaterWidgetId;
      if (rwId != null && typeof rwId === 'number') {
        maxRepeaterWidgetId = Math.max(maxRepeaterWidgetId, rwId);
      }
    });
  });
  const newRepeaterWidgetId = maxRepeaterWidgetId + 1;

  // Calculate repeaterWidgetOrder based on the widget's y-position relative to siblings
  const siblingChildren = (parentContainer.children || [])
    .map((id: string) => currentPage.content[id])
    .filter(Boolean)
    .filter((w: any) => w.id !== addedWidget.id)
    .sort(
      (a: any, b: any) =>
        (a.config.repeaterWidgetOrder ?? 0) -
        (b.config.repeaterWidgetOrder ?? 0)
    );

  let insertIndex = siblingChildren.findIndex(
    (w: any) => addedWidget.config.yLocation < w.config.yLocation
  );
  if (insertIndex === -1) insertIndex = siblingChildren.length;

  const newRepeaterWidgetOrder = insertIndex;

  // Re-index existing siblings whose order is >= the insert index
  siblingChildren.forEach((widget: any, i: number) => {
    const order = i >= insertIndex ? i + 1 : i;
    if (widget.config.repeaterWidgetOrder !== order) {
      dispatch(
        updateWidgetConfig(
          siteId,
          pageId,
          widget.id,
          {
            ...widget.config,
            repeaterWidgetOrder: order,
          },
          false
        )
      );
    }
  });

  // Update the source widget with repeater metadata
  dispatch(
    updateWidgetConfig(
      siteId,
      pageId,
      addedWidget.id,
      {
        ...addedWidget.config,
        repeaterWidgetId: newRepeaterWidgetId,
        repeaterWidgetOrder: newRepeaterWidgetOrder,
      },
      false
    )
  );

  // Clone to sibling containers
  const { xLocation, yLocation } = addedWidget.config;

  repeaterWidget.children.forEach((containerId: string) => {
    if (containerId === parentContainerId) return;

    const container = currentPage.content[containerId];
    if (!container) return;

    const newWidgetId = uuidv4();

    const relativeX = xLocation - parentContainer.config.xLocation;
    const relativeY = yLocation - parentContainer.config.yLocation;

    const targetX = container.config.xLocation + relativeX;
    const targetY = container.config.yLocation + relativeY;

    const newWidgetConfig = {
      ...addedWidget.config,
      parentId: containerId,
      x: targetX,
      y: targetY,
      xLocation: targetX,
      yLocation: targetY,
      repeaterWidgetId: newRepeaterWidgetId,
      repeaterWidgetOrder: newRepeaterWidgetOrder,
    };

    dispatch(
      addChild(
        siteId,
        pageId,
        containerId,
        addedWidget.type,
        newWidgetConfig,
        0,
        newWidgetId,
        addedWidget.tabIndex
      )
    );
  });
}

export const processRepeaterWidgetData = (
  currentPage: PageVersion,
  updateCurrentPage?: (page: PageVersion) => void
): PageVersion => {
  if (!currentPage) return currentPage;

  const repeaterWidgets = Object.values(currentPage.content).filter(
    (widget: any) => widget?.type === widgetTypes.RepeaterWidget
  );

  if (repeaterWidgets.length === 0) return currentPage;

  let hasChanges = false;
  const updatedPage = { ...currentPage };
  updatedPage.content = { ...currentPage.content };

  repeaterWidgets.forEach((repeaterWidget: any) => {
    const repeaterData = repeaterWidget.config?.repeaterData;
    const containers = repeaterWidget.children;

    if (!repeaterData || !Array.isArray(repeaterData.data)) return;

    containers.forEach((containerId: string) => {
      const container = currentPage.content[containerId];

      // Only process visible containers
      if (container?.config?.visible === false) return;

      const dataMapping = repeaterWidget.config?.repeaterDataMapping || {};
      const rowIndex = dataMapping[containerId];
      const rowData = repeaterData?.data?.[rowIndex];

      if (rowIndex == null || !container.children || !rowData) return;

      container.children.forEach((widgetId: string) => {
        const widget = currentPage.content[widgetId];
        if (!widget) return;

        const key = widget.config?.repeaterWidgetKey;
        if (!key || !(key in rowData)) return;

        const value = rowData[key];
        const currentValue =
          widget.type === widgetTypes.Text
            ? widget.config.text
            : widget.type === widgetTypes.Link
            ? widget.config.displayText
            : widget.type === widgetTypes.Image
            ? widget.config.backgroundImage?.url
            : widget.config.displayText;

        if (currentValue !== value) {
          const newConfig = { ...widget.config };

          if (widget.type === widgetTypes.Text) {
            newConfig.text = value;
          } else if (widget.type === widgetTypes.Link) {
            newConfig.displayText = value;
          } else if (widget.type === widgetTypes.Image) {
            newConfig.backgroundImage = {
              ...newConfig.backgroundImage,
              url: value,
            };
          } else {
            newConfig.displayText = value;
          }

          // Create a new widget object to ensure React re-renders
          updatedPage.content[widgetId] = {
            ...widget,
            config: newConfig,
          };
          hasChanges = true;
        }
      });
    });
  });

  if (hasChanges && typeof updateCurrentPage === 'function') {
    updateCurrentPage(updatedPage);
  }

  return hasChanges ? updatedPage : currentPage;
};
